// src/components/accounts/AccountAnalytics.js
// ✅ Apache ECharts revamp: modern cards + real charts + cleaner funnel
// ✅ Adds: Cohort Conversion (30/60/90d) + Top Products Driving Conversion
// ✅ Removes unused imports / ESLint warnings
// ✅ Keeps “worth buying” metrics: spend, conversion, red flags, territory performance, holiday-only, feedback gaps

import React, { useEffect, useMemo, useState } from "react";
import { collection, doc, getDoc, getDocs } from "firebase/firestore";
import { db } from "../../firebase";
import {
  AlertTriangle,
  TrendingUp,
  DollarSign,
  RefreshCw,
  Info,
  Gift,
  Target,
  MapPinned,
  Users,
  CalendarDays,
} from "lucide-react";

// -----------------------------
// Config
// -----------------------------
const REDFLAG_HIGH_SPEND = 250;
const REDFLAG_MANY_ORDERS = 3;
const REDFLAG_ORDERS_30D = 3;
const REDFLAG_BIG_QTY_SINGLE_ORDER = 10;

const WATCHLIST_LIMIT = 5;
const TERRITORY_LIMIT = 10;
const TOP_ACCOUNTS_LIMIT = 8;

const MIN_PRODUCT_SAMPLES_FOR_RANKING = 3;
const TOP_PRODUCTS_LIMIT = 10;

// ✅ JS Date.getMonth() is 0-indexed (Oct=9, Nov=10, Dec=11)
const HOLIDAY_MONTHS = [9, 10, 11];

// -----------------------------
// Helpers
// -----------------------------
function safeLower(v) {
  return String(v || "").toLowerCase();
}

function money(n) {
  const val = Number(n || 0);
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: "USD" }).format(val);
  } catch {
    return `$${val.toFixed(2)}`;
  }
}

function pct(n) {
  const val = Number(n || 0);
  if (!Number.isFinite(val)) return "0%";
  return `${Math.round(val * 100)}%`;
}

function asDate(v) {
  if (!v) return null;
  if (typeof v?.toDate === "function") return v.toDate();
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

function fmtDate(d) {
  if (!d) return "—";
  try {
    return d.toLocaleDateString();
  } catch {
    return "—";
  }
}

function getAccountName(a) {
  return (
    a?.accountName ||
    a?.name ||
    a?.businessName ||
    a?.companyName ||
    a?.title ||
    a?.displayName ||
    "Untitled Account"
  );
}

function getTerritoryLabel(a) {
  return (a?.territoryName || a?.territoryId || "Unassigned").toString();
}

function getAssignedLabel(a) {
  return (a?.assignedToName || a?.assignedToId || "Unassigned").toString();
}

function getVisitDate(account = {}) {
  return asDate(
    account?.lastVisitedAt ||
      account?.visitedAt ||
      account?.lastVisitAt ||
      account?.mostRecentVisitAt ||
      account?.visitDate ||
      null
  );
}

function isDateInRange(date, rangeKey, now = new Date()) {
  if (!date) return false;
  const diff = now.getTime() - date.getTime();
  if (diff < 0) return false;

  const dayMs = 24 * 60 * 60 * 1000;
  if (rangeKey === "week") return diff <= 7 * dayMs;
  if (rangeKey === "year") return diff <= 365 * dayMs;
  return diff <= 30 * dayMs;
}

function inferAccountIdFromOrder(o) {
  return (
    o?.accountId ||
    o?.accountID ||
    o?.account?.id ||
    o?.account?.accountId ||
    o?.accountRefId ||
    o?.accountDocId ||
    o?.accountDocID ||
    null
  );
}

function normalizeRole(v) {
  return String(v || "").trim().toLowerCase();
}

function getAssignedInternalUserId(record = {}) {
  return (
    record?.assignedToId ||
    record?.assignedUserId ||
    record?.repUserId ||
    record?.salesRepId ||
    null
  );
}

function getAssignedInternalUserIds(record = {}) {
  return Array.from(
    new Set(
      [
        getAssignedInternalUserId(record),
        ...(Array.isArray(record?.assignedToUserIds) ? record.assignedToUserIds : []),
        ...(Array.isArray(record?.assignedUserIds) ? record.assignedUserIds : []),
        ...(Array.isArray(record?.assignedTo) ? record.assignedTo : []),
        ...(Array.isArray(record?.userIds) ? record.userIds : []),
        ...(Array.isArray(record?.memberIds) ? record.memberIds : []),
      ]
        .filter(Boolean)
        .map((value) => String(value))
    )
  );
}

function isRecordAssignedToUser(record = {}, userId) {
  if (!userId) return false;
  return getAssignedInternalUserIds(record).includes(String(userId));
}

function getTerritoryId(record = {}) {
  return (
    record?.territoryId ||
    record?.territory?.id ||
    record?.territory?.territoryId ||
    record?.territoryID ||
    null
  );
}

function getUserTerritoryIds(user = {}) {
  return Array.from(
    new Set(
      [
        ...(Array.isArray(user?.territoryIds) ? user.territoryIds : []),
        ...(Array.isArray(user?.territories) ? user.territories : []),
      ]
        .filter(Boolean)
        .map((value) => String(value))
    )
  );
}

function inferConverted(o) {
  const s = safeLower(o?.status);
  if (s.includes("convert")) return true;
  if (o?.isConverted === true) return true;
  if (o?.converted === true) return true;
  if (o?.convertedToOrder === true) return true;
  if (o?.convertedAt) return true;
  if (o?.orderId) return true;
  if (o?.convertedOrderId) return true;
  return false;
}

function inferFeedback(o) {
  if (o?.hasFeedback === true) return true;
  if (o?.feedbackReceived === true) return true;
  if (typeof o?.feedbackText === "string" && o.feedbackText.trim().length) return true;
  if (typeof o?.feedback === "string" && o.feedback.trim().length) return true;
  if (Number(o?.feedbackRating) > 0) return true;
  if (typeof o?.feedbackComments === "string" && o.feedbackComments.trim().length) return true;
  if (typeof o?.feedbackUseCase === "string" && o.feedbackUseCase.trim().length) return true;
  if (o?.rating != null) return true;
  if (o?.review != null) return true;
  return false;
}

function getOrderItems(o) {
  if (Array.isArray(o?.items)) return o.items;
  if (Array.isArray(o?.lineItems)) return o.lineItems;
  if (Array.isArray(o?.samples)) return o.samples;
  if (Array.isArray(o?.products)) return o.products;
  if (Array.isArray(o?.selectedSamples)) return o.selectedSamples;
  return [];
}

function itemQty(it) {
  const q = it?.quantity ?? it?.qty ?? it?.count ?? 1;
  const n = Number(q);
  return Number.isFinite(n) && n > 0 ? n : 1;
}

function itemUnitCost(it, inventoryIndex) {
  const direct =
    it?.unitCost ??
    it?.cost ??
    it?.unitPrice ??
    it?.price ??
    it?.sampleCost ??
    it?.unit_cost ??
    it?.unit_price ??
    it?.cogs;

  const dn = Number(direct);
  if (Number.isFinite(dn) && dn >= 0) return dn;

  const pid = it?.productId || it?.productID || it?.inventoryId || it?.sampleId || it?.id || null;
  if (pid && inventoryIndex[pid]) {
    const inv = inventoryIndex[pid];
    const c = inv?.unitCost ?? inv?.cost ?? inv?.unitPrice ?? inv?.price ?? inv?.sampleCost ?? inv?.cogs;
    const cn = Number(c);
    if (Number.isFinite(cn) && cn >= 0) return cn;
  }

  const sku = it?.sku || it?.SKU || null;
  if (sku && inventoryIndex[`sku:${sku}`]) {
    const inv = inventoryIndex[`sku:${sku}`];
    const c = inv?.unitCost ?? inv?.cost ?? inv?.unitPrice ?? inv?.price ?? inv?.sampleCost ?? inv?.cogs;
    const cn = Number(c);
    if (Number.isFinite(cn) && cn >= 0) return cn;
  }

  if (pid && inventoryIndex[`pid:${pid}`]) {
    const inv = inventoryIndex[`pid:${pid}`];
    const c = inv?.unitCost ?? inv?.cost ?? inv?.unitPrice ?? inv?.price ?? inv?.sampleCost ?? inv?.cogs;
    const cn = Number(c);
    if (Number.isFinite(cn) && cn >= 0) return cn;
  }

  return 0;
}

function orderSpend(o, inventoryIndex) {
  const explicit =
    o?.totalCost ?? o?.sampleTotal ?? o?.totalAmount ?? o?.total ?? o?.totalPrice ?? o?.amount;

  const en = Number(explicit);
  if (Number.isFinite(en) && en >= 0) return en;

  const items = getOrderItems(o);
  if (!items.length) return 0;

  let sum = 0;
  for (const it of items) {
    sum += itemQty(it) * itemUnitCost(it, inventoryIndex);
  }
  return sum;
}

function orderCreatedAt(o) {
  return asDate(
    o?.createdAt || o?.created_on || o?.created || o?.submittedAt || o?.requestedAt || o?.updatedAt || null
  );
}

function totalOrderQty(o) {
  const items = getOrderItems(o);
  return items.reduce((sum, it) => sum + itemQty(it), 0);
}

function yyyymm(d) {
  if (!d) return null;
  const y = d.getFullYear();
  const m = d.getMonth() + 1;
  return `${y}-${String(m).padStart(2, "0")}`;
}

function monthLabelFromKey(key) {
  // key: YYYY-MM
  const [y, m] = String(key || "").split("-").map((x) => Number(x));
  if (!y || !m) return key;
  const date = new Date(y, m - 1, 1);
  try {
    const mon = new Intl.DateTimeFormat(undefined, { month: "short" }).format(date);
    // show year only if it’s not current year
    const nowY = new Date().getFullYear();
    return y === nowY ? mon : `${mon} '${String(y).slice(-2)}`;
  } catch {
    return key;
  }
}

function clampText(s, n = 22) {
  const str = String(s || "");
  if (str.length <= n) return str;
  return `${str.slice(0, n - 1)}…`;
}

// Product key + display name inference
function productKeyFromItem(it) {
  const pid = it?.productId || it?.productID || it?.inventoryId || it?.sampleId || it?.id || null;
  const sku = it?.sku || it?.SKU || null;
  if (pid) return `pid:${pid}`;
  if (sku) return `sku:${sku}`;
  const nm = it?.name || it?.productName || it?.title || it?.label || null;
  if (nm) return `name:${safeLower(nm).trim()}`;
  return null;
}

function productNameFromItem(it, inventoryIndex) {
  const pid = it?.productId || it?.productID || it?.inventoryId || it?.sampleId || it?.id || null;
  const sku = it?.sku || it?.SKU || null;

  if (pid && inventoryIndex[pid]) {
    const inv = inventoryIndex[pid];
    return inv?.name || inv?.title || inv?.productName || inv?.sku || it?.name || "Product";
  }
  if (sku && inventoryIndex[`sku:${sku}`]) {
    const inv = inventoryIndex[`sku:${sku}`];
    return inv?.name || inv?.title || inv?.productName || inv?.sku || it?.name || sku;
  }

  return it?.name || it?.productName || it?.title || it?.label || sku || "Product";
}

// -----------------------------
// UI bits
// -----------------------------
function TinyPill({ children, tone = "slate" }) {
  const tones = {
    slate: "bg-slate-100 text-slate-700 border-slate-200",
    red: "bg-red-50 text-red-700 border-red-200",
    amber: "bg-amber-50 text-amber-800 border-amber-200",
    green: "bg-green-50 text-green-700 border-green-200",
    blue: "bg-blue-50 text-blue-700 border-blue-200",
    purple: "bg-purple-50 text-purple-700 border-purple-200",
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-medium border ${tones[tone] || tones.slate}`}>
      {children}
    </span>
  );
}

function SectionCard({ title, right, children }) {
  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="flex items-center gap-2 border-b border-slate-100 bg-white px-3 py-1.5">
        <div className="text-[13px] font-semibold text-slate-900">{title}</div>
        <div className="ml-auto">{right}</div>
      </div>
      <div className="p-2.5">{children}</div>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, sub, tone = "slate" }) {
  const tones = {
    slate: "bg-slate-50 border-slate-200 text-slate-900",
    green: "bg-green-50 border-green-200 text-slate-900",
    blue: "bg-blue-50 border-blue-200 text-slate-900",
    amber: "bg-amber-50 border-amber-200 text-slate-900",
    red: "bg-red-50 border-red-200 text-slate-900",
  };

  return (
    <div className={`border rounded-lg px-2.5 py-2 ${tones[tone] || tones.slate}`}>
      <div className="flex items-center gap-2">
        {Icon ? (
          <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-white/80 border border-white/70">
            <Icon className="h-3.5 w-3.5 text-slate-700" />
          </div>
        ) : null}
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-[0.14em]">{label}</div>
          <div className="mt-0.5 flex items-end gap-2">
            <div className="text-[1rem] font-extrabold leading-none text-slate-900">{value}</div>
            {sub ? <div className="truncate text-[10px] text-slate-500">{sub}</div> : null}
          </div>
        </div>
      </div>
    </div>
  );
}


function CompactRow({ title, subtitle, value, helper, tone = "slate" }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white px-3 py-2.5">
      <div className="min-w-0">
        <div className="truncate text-sm font-semibold text-slate-900">{title}</div>
        {subtitle ? <div className="truncate text-[11px] text-slate-500">{subtitle}</div> : null}
      </div>
      <div className="shrink-0 text-right">
        <div className="text-sm font-extrabold text-slate-900">{value}</div>
        {helper ? <TinyPill tone={tone}>{helper}</TinyPill> : null}
      </div>
    </div>
  );
}

// -----------------------------
// Component
// -----------------------------
export default function AccountAnalytics({ companyId, refreshKey, currentUserId = null, userProfile = null }) {
  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [inventoryIndex, setInventoryIndex] = useState({});
  const [error, setError] = useState("");
  const [permissionWarnings, setPermissionWarnings] = useState([]);
  const [activeTab, setActiveTab] = useState("watchlist");
  const [visitedAssignee, setVisitedAssignee] = useState("all");
  const [visitedRange] = useState("week");
  const [resolvedUserProfile, setResolvedUserProfile] = useState(userProfile || null);
  const [showPermissionInfo, setShowPermissionInfo] = useState(false);
  const isAdminView = useMemo(() => {
    const role = normalizeRole(resolvedUserProfile?.role);
    return role === "admin" || role === "owner";
  }, [resolvedUserProfile?.role]);
  const isEndUserView = useMemo(() => {
    const role = normalizeRole(resolvedUserProfile?.role);
    return role === "enduser" || role === "end_user";
  }, [resolvedUserProfile?.role]);

  useEffect(() => {
    if (userProfile) {
      setResolvedUserProfile(userProfile);
      return;
    }
    if (!currentUserId) {
      setResolvedUserProfile(null);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, "users", currentUserId));
        if (!cancelled) {
          setResolvedUserProfile(snap.exists() ? { id: snap.id, ...snap.data() } : null);
        }
      } catch {
        if (!cancelled) setResolvedUserProfile(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [currentUserId, userProfile]);

  useEffect(() => {
    if (!companyId) return;

    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        setError("");
        setPermissionWarnings([]);

        const accountsRef = collection(db, "companies", companyId, "accounts");
        const sampleOrdersRef = collection(db, "companies", companyId, "sampleOrders");
        const sampleRequestsRef = collection(db, "companies", companyId, "sampleRequests");
        const inventoryRef = collection(db, "companies", companyId, "inventory");

        let acc = [];
        let so = [];
        let sr = [];
        const idx = {};
        const warnings = [];

        try {
          const accSnap = await getDocs(accountsRef);
          acc = accSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        } catch (e) {
          console.warn("[AccountAnalytics] Could not load accounts:", e?.code || e?.message);
          if (e?.code === "permission-denied") warnings.push("Limited access to accounts data");
        }

        try {
          const soSnap = await getDocs(sampleOrdersRef);
          so = soSnap.docs.map((d) => ({ id: d.id, __collection: "sampleOrders", ...d.data() }));
        } catch (e) {
          if (e?.code !== "permission-denied") {
            console.warn("[AccountAnalytics] Could not load sampleOrders:", e?.code || e?.message);
          }
          if (e?.code === "permission-denied") warnings.push("Limited access to sample orders");
        }

        if (isAdminView) {
          try {
            const srSnap = await getDocs(sampleRequestsRef);
            sr = srSnap.docs.map((d) => ({ id: d.id, __collection: "sampleRequests", ...d.data() }));
          } catch (e) {
            // If you don’t use sampleRequests, safe to ignore.
            console.warn("[AccountAnalytics] Could not load sampleRequests:", e?.code || e?.message);
            if (e?.code === "permission-denied") warnings.push("Limited access to sample requests");
          }
        }

        try {
          const invSnap = await getDocs(inventoryRef);
          invSnap.docs.forEach((d) => {
            const data = { id: d.id, ...d.data() };
            idx[d.id] = data;

            const sku = data?.sku || data?.SKU || null;
            if (sku) idx[`sku:${sku}`] = data;

            const pid = data?.productId || data?.productID || null;
            if (pid) idx[`pid:${pid}`] = data;
          });
        } catch (e) {
          console.warn("[AccountAnalytics] Could not load inventory:", e?.code || e?.message);
          if (e?.code === "permission-denied") warnings.push("Limited access to inventory (cost data may be incomplete)");
        }

        if (cancelled) return;

        const merged = [...so, ...sr];
        const deduped = Array.from(
          new Map(merged.map((o) => [`${o.__collection || "x"}:${o.id}`, o])).values()
        );

        setAccounts(acc);
        setInventoryIndex(idx);
        setOrders(deduped);
        setPermissionWarnings(warnings);

        if (acc.length === 0 && deduped.length === 0) {
          setError("No analytics data available yet (or your role can’t access it).");
        }
      } catch (e) {
        console.error("[AccountAnalytics] load error:", e);
        if (!cancelled) {
          setAccounts([]);
          setOrders([]);
          setInventoryIndex({});
          if (e?.code === "permission-denied") {
            setError("You don't have permission to view analytics. Contact your administrator.");
          } else {
            setError(e?.message || "Failed to load analytics.");
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [companyId, refreshKey, isAdminView]);

  const scopedData = useMemo(() => {
    const roleLower = normalizeRole(resolvedUserProfile?.role);
    const isAdminOrOwner = roleLower === "admin" || roleLower === "owner";
    if (isAdminOrOwner) {
      return { visibleAccounts: accounts, visibleOrders: orders };
    }

    const myUid = currentUserId || resolvedUserProfile?.uid || resolvedUserProfile?.id || null;
    const visibleAccounts = (accounts || []).filter((account) => {
      const createdBy = account?.createdBy || null;
      if (isEndUserView) {
        return isRecordAssignedToUser(account, myUid) || (myUid && createdBy === myUid);
      }

      const territoryId = getTerritoryId(account);
      const myTerritoryIdSet = new Set(getUserTerritoryIds(resolvedUserProfile));
      return isRecordAssignedToUser(account, myUid) || (myUid && createdBy === myUid) || (territoryId && myTerritoryIdSet.has(String(territoryId)));
    });

    const visibleAccountIds = new Set(visibleAccounts.map((account) => String(account.id)));

    const visibleOrders = (orders || []).filter((order) => {
      const accountId = order?.accountId ? String(order.accountId) : null;
      const createdBy = order?.createdBy || null;
      if (isEndUserView) {
        return isRecordAssignedToUser(order, myUid) || (myUid && createdBy === myUid) || (accountId && visibleAccountIds.has(accountId));
      }

      const myTerritoryIdSet = new Set(getUserTerritoryIds(resolvedUserProfile));
      const orderTerritoryId = getTerritoryId(order);
      const accountTerritoryId =
        accountId && visibleAccountIds.has(accountId)
          ? getTerritoryId(visibleAccounts.find((account) => String(account.id) === accountId) || {})
          : null;

      return (
        isRecordAssignedToUser(order, myUid) ||
        (myUid && createdBy === myUid) ||
        (accountId && visibleAccountIds.has(accountId)) ||
        (orderTerritoryId && myTerritoryIdSet.has(String(orderTerritoryId))) ||
        (accountTerritoryId && myTerritoryIdSet.has(String(accountTerritoryId)))
      );
    });

    return { visibleAccounts, visibleOrders };
  }, [accounts, currentUserId, isEndUserView, orders, resolvedUserProfile]);

  const assigneeOptions = useMemo(() => {
    const options = new Map();
    (scopedData.visibleAccounts || []).forEach((account) => {
      const assignedId = account?.assignedToId || account?.assignedUserId || null;
      const assignedName = account?.assignedToName || account?.assignedUserName || null;
      if (!assignedId || !assignedName) return;
      options.set(String(assignedId), assignedName);
    });
    return Array.from(options.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [scopedData.visibleAccounts]);

  const visitedMetrics = useMemo(() => {
    const now = new Date();
    const source = (scopedData.visibleAccounts || []).filter((account) => {
      if (visitedAssignee === "all") return true;
      return String(account?.assignedToId || account?.assignedUserId || "") === visitedAssignee;
    });

    const visitedRows = source
      .map((account) => {
        const visitedAt = getVisitDate(account);
        return {
          id: account?.id,
          name: getAccountName(account),
          assigned: getAssignedLabel(account),
          territory: getTerritoryLabel(account),
          visitedAt,
          inRange: isDateInRange(visitedAt, visitedRange, now),
        };
      })
      .filter((row) => row.inRange)
      .sort((a, b) => (b.visitedAt?.getTime?.() || 0) - (a.visitedAt?.getTime?.() || 0));

    const visitedCount = visitedRows.length;
    const totalCount = source.length;
    const unvisitedCount = Math.max(totalCount - visitedCount, 0);

    return {
      visitedCount,
      totalCount,
      unvisitedCount,
      coverage: totalCount > 0 ? visitedCount / totalCount : 0,
      rows: visitedRows.slice(0, 5),
    };
  }, [scopedData.visibleAccounts, visitedAssignee, visitedRange]);

  const computed = useMemo(() => {
    const sourceAccounts = scopedData.visibleAccounts || [];
    const sourceOrders = scopedData.visibleOrders || [];
    const totalAccounts = sourceAccounts.length;
    const activeAccounts = sourceAccounts.filter((a) => a.active !== false).length;

    const accountMap = {};
    sourceAccounts.forEach((a) => (accountMap[a.id] = a));

    const nameFromOrders = {};
    const accountOrderMonths = {};
    const aggByAccount = {};

    const now = new Date();
    const ms30d = 30 * 24 * 60 * 60 * 1000;
    const ms60d = 60 * 24 * 60 * 60 * 1000;
    const ms90d = 90 * 24 * 60 * 60 * 1000;

    // last 6 months buckets (including zeros)
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    const monthKeys = [];
    const monthlyData = {}; // key -> { orders, spend, conversions }

    for (let i = 5; i >= 0; i--) {
      const date = new Date(currentYear, currentMonth - i, 1);
      const k = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
      monthKeys.push(k);
      monthlyData[k] = { orders: 0, spend: 0, conversions: 0 };
    }

    // Cohort and product analytics
    const firstSampleAtByAccount = {};  // accountId -> Date
    const firstConversionAtByAccount = {}; // accountId -> Date (earliest converted order)
    const productsByAccount = {}; // accountId -> Set(productKey)
    const productNameIndex = {}; // productKey -> displayName

    for (const o of sourceOrders) {
      const accountId = inferAccountIdFromOrder(o);
      if (!accountId) continue;

      if (o?.accountName) nameFromOrders[accountId] = o.accountName;

      const spend = orderSpend(o, inventoryIndex);
      const converted = inferConverted(o);
      const hasFeedback = inferFeedback(o);
      const createdAt = orderCreatedAt(o);
      const qty = totalOrderQty(o);

      // Track order month for holiday detection + trend chart
      if (createdAt) {
        if (!accountOrderMonths[accountId]) accountOrderMonths[accountId] = new Set();
        accountOrderMonths[accountId].add(createdAt.getMonth());

        const key = `${createdAt.getFullYear()}-${String(createdAt.getMonth() + 1).padStart(2, "0")}`;
        if (monthlyData[key]) {
          monthlyData[key].orders += 1;
          monthlyData[key].spend += spend;
          if (converted) monthlyData[key].conversions += 1;
        }

        // cohort: first sample date
        if (!firstSampleAtByAccount[accountId] || createdAt < firstSampleAtByAccount[accountId]) {
          firstSampleAtByAccount[accountId] = createdAt;
        }

        // cohort: first conversion date (earliest converted order)
        if (converted) {
          if (!firstConversionAtByAccount[accountId] || createdAt < firstConversionAtByAccount[accountId]) {
            firstConversionAtByAccount[accountId] = createdAt;
          }
        }
      }

      // account aggregation
      if (!aggByAccount[accountId]) {
        aggByAccount[accountId] = {
          accountId,
          sampleOrders: 0,
          sampleSpend: 0,
          convertedOrders: 0,
          ordersWithFeedback: 0,
          lastSampleAt: null,
          ordersLast30d: 0,
          anyRatingLow: false,
          anyBigQtyOrder: false,
        };
      }

      const agg = aggByAccount[accountId];
      agg.sampleOrders += 1;
      agg.sampleSpend += spend;
      if (converted) agg.convertedOrders += 1;
      if (hasFeedback) agg.ordersWithFeedback += 1;

      const r = Number(o?.rating);
      if (Number.isFinite(r) && r > 0 && r <= 2) agg.anyRatingLow = true;

      if (qty >= REDFLAG_BIG_QTY_SINGLE_ORDER) agg.anyBigQtyOrder = true;

      if (createdAt) {
        if (!agg.lastSampleAt || createdAt > agg.lastSampleAt) agg.lastSampleAt = createdAt;
        if (now.getTime() - createdAt.getTime() <= ms30d) agg.ordersLast30d += 1;
      }

      // products per account (account-level, not per order)
      const items = getOrderItems(o);
      if (!productsByAccount[accountId]) productsByAccount[accountId] = new Set();

      for (const it of items) {
        const key = productKeyFromItem(it);
        if (!key) continue;
        productsByAccount[accountId].add(key);

        if (!productNameIndex[key]) {
          productNameIndex[key] = productNameFromItem(it, inventoryIndex);
        }
      }
    }

    // Identify holiday-only accounts
    const holidayOnlyAccounts = new Set();
    Object.entries(accountOrderMonths).forEach(([accountId, months]) => {
      const monthArray = Array.from(months);
      if (monthArray.length > 0 && monthArray.every((m) => HOLIDAY_MONTHS.includes(m))) {
        holidayOnlyAccounts.add(accountId);
      }
    });

    const sampledAccounts = Object.values(aggByAccount).filter((x) => x.sampleOrders > 0).length;
    const convertedAccounts = Object.values(aggByAccount).filter((x) => x.sampleOrders > 0 && x.convertedOrders > 0).length;
    const accountsWithFeedback = Object.values(aggByAccount).filter((x) => x.ordersWithFeedback > 0).length;

    const totalSampleSpend = Object.values(aggByAccount).reduce((sum, x) => sum + (x.sampleSpend || 0), 0);
    const overallConversionRate = sampledAccounts > 0 ? convertedAccounts / sampledAccounts : 0;
    const avgSpendPerSampled = sampledAccounts > 0 ? totalSampleSpend / sampledAccounts : 0;

    // Territory performance
    const territoryStats = {};
    for (const a of sourceAccounts) {
      const terr = getTerritoryLabel(a);
      if (!territoryStats[terr]) {
        territoryStats[terr] = { territory: terr, totalAccounts: 0, sampledAccounts: 0, convertedAccounts: 0, sampleSpend: 0 };
      }
      const t = territoryStats[terr];
      t.totalAccounts += 1;

      const agg = aggByAccount[a.id];
      if (agg && agg.sampleOrders > 0) {
        t.sampledAccounts += 1;
        t.sampleSpend += agg.sampleSpend || 0;
        if (agg.convertedOrders > 0) t.convertedAccounts += 1;
      }
    }

    const territoryRows = Object.values(territoryStats)
      .map((t) => ({
        ...t,
        conversionRate: t.sampledAccounts > 0 ? t.convertedAccounts / t.sampledAccounts : 0,
        avgSpend: t.sampledAccounts > 0 ? t.sampleSpend / t.sampledAccounts : 0,
      }))
      .sort((a, b) => b.sampleSpend - a.sampleSpend)
      .slice(0, TERRITORY_LIMIT);

    const strongestTerritory = territoryRows
      .filter((row) => row.sampledAccounts > 0)
      .sort((a, b) => {
        if ((b.conversionRate || 0) !== (a.conversionRate || 0)) return (b.conversionRate || 0) - (a.conversionRate || 0);
        return (b.sampleSpend || 0) - (a.sampleSpend || 0);
      })[0] || null;

    const weakestTerritory = territoryRows
      .filter((row) => row.sampledAccounts > 0)
      .sort((a, b) => {
        if ((a.conversionRate || 0) !== (b.conversionRate || 0)) return (a.conversionRate || 0) - (b.conversionRate || 0);
        return (b.sampleSpend || 0) - (a.sampleSpend || 0);
      })[0] || null;

    // Top accounts
    const accountSpendRows = Object.values(aggByAccount)
      .map((x) => {
        const a = accountMap[x.accountId] || null;
        const name = a ? getAccountName(a) : nameFromOrders[x.accountId] || "Account (restricted)";
        return {
          accountId: x.accountId,
          name,
          territory: getTerritoryLabel(a),
          assigned: getAssignedLabel(a),
          sampleOrders: x.sampleOrders,
          sampleSpend: x.sampleSpend,
          converted: x.convertedOrders > 0,
          lastSampleAt: x.lastSampleAt,
          isHolidayOnly: holidayOnlyAccounts.has(x.accountId),
        };
      })
      .sort((a, b) => b.sampleSpend - a.sampleSpend)
      .slice(0, TOP_ACCOUNTS_LIMIT);

    // Watchlists
    const noConversion = Object.values(aggByAccount)
      .filter((x) => x.sampleOrders > 0 && x.convertedOrders === 0)
      .map((x) => {
        const a = accountMap[x.accountId] || null;
        const name = a ? getAccountName(a) : nameFromOrders[x.accountId] || "Account (restricted)";
        return {
          accountId: x.accountId,
          name,
          territory: getTerritoryLabel(a),
          assigned: getAssignedLabel(a),
          sampleOrders: x.sampleOrders,
          sampleSpend: x.sampleSpend,
          lastSampleAt: x.lastSampleAt,
          isHolidayOnly: holidayOnlyAccounts.has(x.accountId),
        };
      })
      .sort((a, b) => (b.sampleSpend || 0) - (a.sampleSpend || 0))
      .slice(0, WATCHLIST_LIMIT);

    const noFeedback = Object.values(aggByAccount)
      .filter((x) => x.sampleOrders > 0 && x.ordersWithFeedback === 0)
      .map((x) => {
        const a = accountMap[x.accountId] || null;
        const name = a ? getAccountName(a) : nameFromOrders[x.accountId] || "Account (restricted)";
        return {
          accountId: x.accountId,
          name,
          territory: getTerritoryLabel(a),
          assigned: getAssignedLabel(a),
          sampleOrders: x.sampleOrders,
          sampleSpend: x.sampleSpend,
          lastSampleAt: x.lastSampleAt,
          isHolidayOnly: holidayOnlyAccounts.has(x.accountId),
        };
      })
      .sort((a, b) => (b.sampleOrders || 0) - (a.sampleOrders || 0))
      .slice(0, WATCHLIST_LIMIT);

    const redFlags = Object.values(aggByAccount)
      .map((x) => {
        const a = accountMap[x.accountId] || null;
        const name = a ? getAccountName(a) : nameFromOrders[x.accountId] || "Account (restricted)";

        const flags = [];
        if (x.convertedOrders === 0 && x.sampleSpend >= REDFLAG_HIGH_SPEND) flags.push(`High spend (${money(x.sampleSpend)}) no conversion`);
        if (x.convertedOrders === 0 && x.sampleOrders >= REDFLAG_MANY_ORDERS) flags.push(`${x.sampleOrders} orders, no conversion`);
        if (x.ordersWithFeedback === 0 && x.sampleOrders >= 2) flags.push("No feedback");
        if (x.ordersLast30d >= REDFLAG_ORDERS_30D && x.convertedOrders === 0) flags.push(`${x.ordersLast30d} orders in 30d`);
        if (x.anyRatingLow) flags.push("Low rating");
        if (x.anyBigQtyOrder) flags.push(`Large order (qty ≥ ${REDFLAG_BIG_QTY_SINGLE_ORDER})`);
        if (!flags.length) return null;

        return {
          accountId: x.accountId,
          name,
          territory: getTerritoryLabel(a),
          assigned: getAssignedLabel(a),
          sampleOrders: x.sampleOrders,
          sampleSpend: x.sampleSpend,
          lastSampleAt: x.lastSampleAt,
          flags,
          isHolidayOnly: holidayOnlyAccounts.has(x.accountId),
        };
      })
      .filter(Boolean)
      .sort((a, b) => (b.sampleSpend || 0) - (a.sampleSpend || 0))
      .slice(0, WATCHLIST_LIMIT);

    // Trend series (last 6 months)
    const monthLabels = monthKeys.map(monthLabelFromKey);
    const ordersSeries = monthKeys.map((k) => monthlyData[k]?.orders || 0);
    const spendSeries = monthKeys.map((k) => Math.round(monthlyData[k]?.spend || 0));
    const convSeries = monthKeys.map((k) => monthlyData[k]?.conversions || 0);

    // Funnel stages
    const funnelStages = [
      { name: "Total Accounts", value: totalAccounts },
      { name: "Active Accounts", value: activeAccounts },
      { name: "Sampled Accounts", value: sampledAccounts },
      { name: "Converted Accounts", value: convertedAccounts },
    ];

    // -----------------------------
    // Cohort Conversion (30/60/90d)
    // cohort = month of FIRST sample per account
    // -----------------------------
    const cohortBuckets = {}; // YYYY-MM -> { total, conv30, conv60, conv90 }
    const cohortKeysSet = new Set();

    Object.entries(firstSampleAtByAccount).forEach(([accountId, firstSampleAt]) => {
      const cohortKey = yyyymm(firstSampleAt);
      if (!cohortKey) return;

      cohortKeysSet.add(cohortKey);
      if (!cohortBuckets[cohortKey]) cohortBuckets[cohortKey] = { total: 0, conv30: 0, conv60: 0, conv90: 0 };

      cohortBuckets[cohortKey].total += 1;

      const convAt = firstConversionAtByAccount[accountId] || null;
      if (!convAt) return;

      const diff = convAt.getTime() - firstSampleAt.getTime();
      if (diff <= ms30d) cohortBuckets[cohortKey].conv30 += 1;
      if (diff <= ms60d) cohortBuckets[cohortKey].conv60 += 1;
      if (diff <= ms90d) cohortBuckets[cohortKey].conv90 += 1;
    });

    // Keep it readable: last 8 cohorts max (sorted chronologically)
    const cohortKeys = Array.from(cohortKeysSet)
      .sort((a, b) => (a < b ? -1 : 1))
      .slice(-8);

    const cohortLabels = cohortKeys.map(monthLabelFromKey);
    const cohortRate30 = cohortKeys.map((k) => {
      const b = cohortBuckets[k];
      if (!b || !b.total) return 0;
      return Math.round((b.conv30 / b.total) * 100);
    });
    const cohortRate60 = cohortKeys.map((k) => {
      const b = cohortBuckets[k];
      if (!b || !b.total) return 0;
      return Math.round((b.conv60 / b.total) * 100);
    });
    const cohortRate90 = cohortKeys.map((k) => {
      const b = cohortBuckets[k];
      if (!b || !b.total) return 0;
      return Math.round((b.conv90 / b.total) * 100);
    });

    // -----------------------------
    // Top Products Driving Conversion (account-level)
    // For each product: sampledAccounts, convertedAccounts, conversionRate
    // -----------------------------
    const productStats = {}; // key -> { name, sampled, converted }
    const accountConverted = {};
    Object.values(aggByAccount).forEach((a) => {
      accountConverted[a.accountId] = a.convertedOrders > 0;
    });

    Object.entries(productsByAccount).forEach(([accountId, productKeysSet]) => {
      const didConvert = accountConverted[accountId] === true;
      for (const pKey of productKeysSet) {
        if (!productStats[pKey]) {
          productStats[pKey] = { key: pKey, name: productNameIndex[pKey] || "Product", sampled: 0, converted: 0 };
        }
        productStats[pKey].sampled += 1;
        if (didConvert) productStats[pKey].converted += 1;
      }
    });

    const topProductsRows = Object.values(productStats)
      .map((p) => ({
        ...p,
        conversionRate: p.sampled > 0 ? p.converted / p.sampled : 0,
      }))
      .filter((p) => p.sampled >= MIN_PRODUCT_SAMPLES_FOR_RANKING)
      .sort((a, b) => {
        // prioritize real impact, then rate
        if ((b.converted || 0) !== (a.converted || 0)) return (b.converted || 0) - (a.converted || 0);
        return (b.conversionRate || 0) - (a.conversionRate || 0);
      })
      .slice(0, TOP_PRODUCTS_LIMIT);

    const topProductNames = topProductsRows.map((p) => clampText(p.name, 26));
    const topProductRates = topProductsRows.map((p) => Math.round((p.conversionRate || 0) * 100));
    const topProductSampled = topProductsRows.map((p) => p.sampled || 0);
    const topProductConverted = topProductsRows.map((p) => p.converted || 0);

    return {
      totalAccounts,
      activeAccounts,
      sampledAccounts,
      convertedAccounts,
      accountsWithFeedback,
      overallConversionRate,
      totalSampleSpend,
      avgSpendPerSampled,
      strongestTerritory,
      weakestTerritory,
      territoryRows,
      accountSpendRows,
      watch: { noConversion, noFeedback, redFlags },
      holidayOnlyAccounts,
      holidayOnlyCount: holidayOnlyAccounts.size,
      monthLabels,
      ordersSeries,
      spendSeries,
      convSeries,
      funnelStages,

      cohortLabels,
      cohortRate30,
      cohortRate60,
      cohortRate90,

      topProductsRows,
      topProductNames,
      topProductRates,
      topProductSampled,
      topProductConverted,
    };
  }, [inventoryIndex, scopedData.visibleAccounts, scopedData.visibleOrders]);

  if (loading) {
    return (
      <div className="p-3 text-sm text-slate-600 flex items-center gap-2">
        <RefreshCw className="w-4 h-4 animate-spin" />
        Loading analytics…
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-3">
        <div className="bg-red-50 border border-red-200 text-red-800 px-3 py-2 rounded-lg text-sm">
          {error}
        </div>
      </div>
    );
  }

  const watchCount =
    (computed.watch.noConversion?.length || 0) + (computed.watch.noFeedback?.length || 0);

  return (
    <div className="space-y-5">
      {/* Permission warnings */}
      {!isAdminView && permissionWarnings.length > 0 && (
        <div className="flex justify-end">
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowPermissionInfo((prev) => !prev)}
              className="inline-flex items-center justify-center rounded-full border border-blue-200 bg-blue-50 p-2 text-blue-700 hover:bg-blue-100"
              title="Partial data view"
            >
              <Info className="h-4 w-4" />
            </button>
            {showPermissionInfo ? (
              <div className="absolute right-0 top-11 z-10 w-72 rounded-2xl border border-blue-200 bg-white p-3 text-left shadow-xl">
                <div className="text-sm font-semibold text-slate-900">Partial data view</div>
                <div className="mt-1 text-xs text-slate-600">
                  {permissionWarnings.join(". ")}. Analytics reflect only the data you can access.
                </div>
              </div>
            ) : null}
          </div>
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white p-2.5 shadow-sm">
        <div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Overview</div>
            <div className="mt-0.5 text-[12px] text-slate-500">Sample performance and visit coverage in one compact strip.</div>
          </div>
          <div className="flex items-center gap-1.5">
            <select
              value={visitedAssignee}
              onChange={(e) => setVisitedAssignee(e.target.value)}
              className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-[11px] font-medium text-slate-700"
            >
              <option value="all">All assignees</option>
              {assigneeOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
            </select>
            <TinyPill tone="blue">Current week</TinyPill>
          </div>
        </div>

        <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-3">
          <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
            <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Sampled Accounts</div>
            <div className="mt-1 flex items-end justify-between gap-3">
              <div className="text-lg font-extrabold leading-none text-slate-900">{computed.sampledAccounts}</div>
              <div className="text-[11px] text-slate-500 text-right">{computed.totalAccounts} tracked accounts</div>
            </div>
          </div>

          <div className="rounded-xl border border-blue-200 bg-blue-50 px-3 py-2">
            <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-blue-700">Converted Accounts</div>
            <div className="mt-1 flex items-end justify-between gap-3">
              <div className="text-lg font-extrabold leading-none text-slate-900">{computed.convertedAccounts}</div>
              <div className="text-[11px] text-blue-700 text-right">{pct(computed.overallConversionRate)} conversion rate</div>
            </div>
          </div>

          <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2">
            <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-emerald-700">Feedback Coverage</div>
            <div className="mt-1 flex items-end justify-between gap-3">
              <div className="text-lg font-extrabold leading-none text-slate-900">{pct(computed.accountsWithFeedback / Math.max(computed.sampledAccounts, 1))}</div>
              <div className="text-[11px] text-emerald-700 text-right">{computed.accountsWithFeedback} accounts with feedback</div>
            </div>
          </div>
        </div>

        <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-3">
          <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
            <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Visited This Week</div>
            <div className="mt-1 flex items-end justify-between gap-3">
              <div className="text-base font-extrabold leading-none text-slate-900">{visitedMetrics.visitedCount}</div>
              <div className="text-[11px] text-slate-500 text-right">accounts</div>
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
            <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Visit Coverage</div>
            <div className="mt-1 flex items-end justify-between gap-3">
              <div className="text-base font-extrabold leading-none text-slate-900">{pct(visitedMetrics.coverage)}</div>
              <div className="text-[11px] text-slate-500 text-right">current week</div>
            </div>
          </div>

          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
            <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-amber-700">Need Visit</div>
            <div className="mt-1 flex items-end justify-between gap-3">
              <div className="text-base font-extrabold leading-none text-slate-900">{visitedMetrics.unvisitedCount}</div>
              <div className="text-[11px] text-amber-700 text-right">accounts</div>
            </div>
          </div>
        </div>

      </div>

      {/* KPI Row */}
      <div className={`grid grid-cols-2 gap-1.5 ${isEndUserView ? "xl:grid-cols-4" : "xl:grid-cols-5"}`}>
        {!isEndUserView ? (
          <KpiCard
            icon={DollarSign}
            label="Total Sample Spend"
            value={money(computed.totalSampleSpend)}
            sub={`Avg/account: ${money(computed.avgSpendPerSampled)}`}
            tone="green"
          />
        ) : null}
        <KpiCard
          icon={Target}
          label="Conversion Rate"
          value={pct(computed.overallConversionRate)}
          sub={`${computed.convertedAccounts}/${computed.sampledAccounts} sampled`}
          tone="blue"
        />
        <KpiCard
          icon={TrendingUp}
          label="Active Accounts"
          value={computed.activeAccounts}
          sub={`of ${computed.totalAccounts} total`}
          tone="slate"
        />
        <KpiCard
          icon={AlertTriangle}
          label="Red Flags"
          value={computed.watch.redFlags.length}
          sub={isEndUserView ? "Accounts needing attention" : "Revenue leakage risks"}
          tone={computed.watch.redFlags.length ? "red" : "slate"}
        />
        <KpiCard
          icon={Gift}
          label="Holiday-only"
          value={computed.holidayOnlyCount}
          sub="Seasonal sample buyers"
          tone="amber"
        />
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-12">
        <div className="space-y-4 xl:col-span-4">
          <SectionCard title="Coverage Cues" right={<TinyPill tone="blue">Account ops</TinyPill>}>
            <div className="space-y-2">
              <CompactRow
                title="Sample penetration"
                subtitle="Accounts that have ever received samples"
                value={pct(computed.sampledAccounts / Math.max(computed.totalAccounts, 1))}
                helper={`${computed.sampledAccounts}/${computed.totalAccounts}`}
                tone="blue"
              />
              <CompactRow
                title="Response coverage"
                subtitle="Accounts with any feedback on samples"
                value={pct(computed.accountsWithFeedback / Math.max(computed.sampledAccounts, 1))}
                helper={`${computed.accountsWithFeedback} accounts`}
                tone="green"
              />
              <CompactRow
                title="Seasonal-only buyers"
                subtitle="Accounts only sampling Oct–Dec"
                value={computed.holidayOnlyCount}
                helper="watch for off-season gaps"
                tone="amber"
              />
            </div>
          </SectionCard>
        </div>

        <div className="space-y-4 xl:col-span-4">
          <SectionCard title="Recent Visits This Week" right={<TinyPill tone="blue">Current week</TinyPill>}>
            <div className="space-y-1.5">
              {visitedMetrics.rows.length ? (
                visitedMetrics.rows.map((row) => (
                  <div key={row.id} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2">
                    <div className="min-w-0">
                      <div className="truncate text-sm font-semibold text-slate-900">{row.name}</div>
                      <div className="truncate text-[11px] text-slate-500">{row.assigned} • {row.territory}</div>
                    </div>
                    <div className="shrink-0 text-right">
                      <div className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-700">
                        <CalendarDays className="h-3.5 w-3.5 text-slate-400" />
                        {fmtDate(row.visitedAt)}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="rounded-lg bg-slate-50 px-3 py-2 text-[12px] text-slate-500">
                  No account visits were logged this week for the current assignee filter.
                </div>
              )}
            </div>
          </SectionCard>
        </div>

        <div className="space-y-2 xl:col-span-4">
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-slate-900">Action Center</div>
                <div className="text-xs text-slate-500">Compact watchlists for accounts that need follow-through.</div>
              </div>
              <TinyPill tone="red">Needs Attention</TinyPill>
            </div>

            <div className="inline-flex flex-wrap overflow-hidden rounded-lg bg-slate-100">
              {[
                { id: "watchlist", label: "Watchlist", count: watchCount },
                ...(!isEndUserView ? [{ id: "redflags", label: "Red Flags", count: computed.watch.redFlags.length }] : []),
                { id: "holiday", label: "Holiday", count: computed.holidayOnlyCount },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${
                    activeTab === tab.id
                      ? "bg-white text-slate-900 shadow-[inset_3px_0_0_0_rgb(37,99,235)]"
                      : "text-slate-600 hover:text-slate-900 hover:bg-white/50"
                  }`}
                >
                  <span>{tab.label}</span>
                  <span className={`inline-flex min-w-5 items-center justify-center rounded-full px-1.5 py-0.5 text-[10px] ${
                    activeTab === tab.id ? "bg-blue-100 text-blue-700" : "bg-slate-200 text-slate-700"
                  }`}>
                    {tab.count}
                  </span>
                </button>
              ))}
            </div>

            {activeTab === "watchlist" && (
              <div className="space-y-2">
                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <div className="text-xs font-bold text-slate-900">No Conversion</div>
                    <TinyPill tone="red">{computed.watch.noConversion.length}</TinyPill>
                  </div>
                  <div className="space-y-1">
                    {computed.watch.noConversion.map((r) => (
                      <div key={r.accountId} className="flex items-center justify-between gap-3 rounded-lg px-2 py-1 hover:bg-slate-50">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 min-w-0">
                            <div className="truncate text-xs font-semibold text-slate-900">{r.name}</div>
                            {r.isHolidayOnly && <Gift className="w-3.5 h-3.5 text-amber-500" />}
                          </div>
                          <div className="text-[10px] text-slate-500">
                            {isEndUserView ? `Last sample ${fmtDate(r.lastSampleAt)}` : `${money(r.sampleSpend)} • ${fmtDate(r.lastSampleAt)}`}
                          </div>
                        </div>
                        <div className="shrink-0 text-right text-[11px] font-semibold text-slate-700">
                          {r.sampleOrders}
                          <div className="text-[10px] font-medium text-slate-400">orders</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <div className="text-xs font-bold text-slate-900">No Feedback</div>
                    <TinyPill tone="amber">{computed.watch.noFeedback.length}</TinyPill>
                  </div>
                  <div className="space-y-1">
                    {computed.watch.noFeedback.map((r) => (
                      <div key={r.accountId} className="flex items-center justify-between gap-3 rounded-lg px-2 py-1 hover:bg-slate-50">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 min-w-0">
                            <div className="truncate text-xs font-semibold text-slate-900">{r.name}</div>
                            {r.isHolidayOnly && <Gift className="w-3.5 h-3.5 text-amber-500" />}
                          </div>
                          <div className="text-[10px] text-slate-500">{r.sampleOrders} orders • last {fmtDate(r.lastSampleAt)}</div>
                        </div>
                        <div className="shrink-0 text-[11px] font-semibold text-amber-700">Pending</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {!isEndUserView && activeTab === "redflags" && (
              <div className="space-y-1">
                {computed.watch.redFlags.map((r) => (
                  <div key={r.accountId} className="flex items-start justify-between gap-3 rounded-lg px-2 py-1.5 hover:bg-red-50/60">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="truncate text-xs font-extrabold text-slate-900">{r.name}</div>
                        {r.isHolidayOnly && <Gift className="w-4 h-4 text-amber-500" />}
                      </div>
                      <div className="text-[10px] text-slate-500">{r.territory} • {r.sampleOrders} orders</div>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {r.flags.map((f, idx) => (
                          <span key={idx} className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] text-red-800">{f}</span>
                        ))}
                      </div>
                    </div>
                    <div className="shrink-0 text-xs font-extrabold text-red-700">{money(r.sampleSpend)}</div>
                  </div>
                ))}
              </div>
            )}

            {activeTab === "holiday" && (
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-xs text-slate-600">
                  <Gift className="w-4 h-4 text-amber-500" />
                  Accounts that only sample in Oct–Dec (seasonal buyers)
                </div>
                {Array.from(computed.holidayOnlyAccounts).slice(0, 12).map((accountId) => {
                  const account = accounts.find((a) => a.id === accountId);
                  const row = computed.accountSpendRows.find((r) => r.accountId === accountId);
                  if (!account && !row) return null;

                  return (
                    <div key={accountId} className="flex items-center justify-between gap-3 rounded-lg px-2 py-1 hover:bg-amber-50/60">
                      <div className="min-w-0">
                        <div className="truncate text-xs font-extrabold text-slate-900">
                          {account ? getAccountName(account) : row?.name}
                        </div>
                        <div className="truncate text-[10px] text-slate-500">
                          {account ? getTerritoryLabel(account) : row?.territory}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs font-bold text-amber-800">{row ? `${row.sampleOrders} orders` : "—"}</div>
                        {!isEndUserView ? <div className="text-[10px] text-slate-500">{row ? money(row.sampleSpend) : ""}</div> : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {!isEndUserView ? (
              <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm">
                <div className="text-[11px] font-medium text-slate-500 uppercase">Avg Spend / Sampled Acct</div>
                <div className="text-xl font-extrabold text-slate-900 mt-1">{money(computed.avgSpendPerSampled)}</div>
                <div className="text-[11px] text-slate-500 mt-1">Controls COGS & leakage</div>
              </div>
            ) : null}
            <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm">
              <div className="text-[11px] font-medium text-slate-500 uppercase">Sampling Rate</div>
              <div className="text-xl font-extrabold text-slate-900 mt-1">
                {pct(computed.sampledAccounts / Math.max(computed.totalAccounts, 1))}
              </div>
              <div className="text-[11px] text-slate-500 mt-1">Activity & adoption</div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex items-center gap-2 text-[11px] font-medium uppercase text-slate-500">
                <MapPinned className="h-4 w-4 text-slate-500" />
                Best Territory
              </div>
              <div className="mt-2 text-base font-extrabold text-slate-900">
                {computed.strongestTerritory?.territory || "—"}
              </div>
              <div className="text-[11px] text-slate-500">
                {computed.strongestTerritory ? `${pct(computed.strongestTerritory.conversionRate)} conversion` : "Waiting on more data"}
              </div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex items-center gap-2 text-[11px] font-medium uppercase text-slate-500">
                <Users className="h-4 w-4 text-slate-500" />
                Feedback Reach
              </div>
              <div className="mt-2 text-base font-extrabold text-slate-900">{computed.accountsWithFeedback}</div>
              <div className="text-[11px] text-slate-500">accounts have sample feedback on file</div>
            </div>
          </div>

          <div className="text-[10px] text-slate-500 flex items-start gap-2">
            <Info className="w-3.5 h-3.5 mt-0.5" />
            <span>
              Holiday-only accounts are marked with <Gift className="w-3.5 h-3.5 inline text-amber-500" />.
              Spend uses order totals when available; otherwise it estimates from line items + inventory costs.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
